// Every Firestore access for propose and confirm. Views never touch `db`.
// Rules in proposalRules.js; seat arithmetic shared with bookingService.
//
// Web SDK v1: every read before any write, and every document read inside a
// transaction is written in it. Queries (the deliveryList lookup) cannot run
// inside one, so they run just before it -- the same trade removeTripProvince
// makes -- and confirm re-checks.
const { db } = require('@/firebase')
const R = require('./bookingRules')
const X = require('./proposalRules')
const V = require('./visitRules')
const S = require('./bookingService')
const U = require('./upcountry')
const US = require('./upcountryService')

const DAY_REF = date => db.collection('bookingDay').doc(date)
const SLOT_COL = () => db.collection('bookingSlot')
const TRIP_COL = () => db.collection('outOfAreaTrip')

// A job already on a dispatch sheet belongs to delivery. carBooking writes
// deliveryList with the SO as a string, the same form bookingSlot stores.
async function isDispatched (soNo) {
  const so = soNo == null ? '' : String(soNo).trim()
  if (!so) return false
  const snap = await db.collection('deliveryList').where('soNo', '==', so).limit(1).get()
  return !snap.empty
}

async function preRead (bookingId) {
  const snap = await SLOT_COL().doc(bookingId).get()
  if (!snap.exists) throw new R.BookingError('NOT_FOUND')
  return snap.data()
}

function deciderCtx (b, { role, identities, user, profiles }) {
  return {
    role,
    identities,
    callerProfile: X.profileOf(profiles, user),
    ownerProfile: X.profileOf(profiles, b.user)
  }
}

async function proposeMove (bookingId, { date, slot, reason, note, user, role, identities }) {
  if (R.SLOTS.indexOf(slot) === -1) throw new R.BookingError('BAD_SLOT')
  if (!String(reason || '').trim()) throw new R.BookingError('NO_REASON')
  const dispatched = await isDispatched((await preRead(bookingId)).soNo)
  const cfg = await S.getConfig()
  const bRef = SLOT_COL().doc(bookingId)
  let result = {}

  await db.runTransaction(async t => {
    const bSnap = await t.get(bRef)
    if (!bSnap.exists) throw new R.BookingError('NOT_FOUND')
    const b = bSnap.data()
    if (U.isUpcountry(b)) throw new R.BookingError('UPCOUNTRY_PATH')
    const gate = X.checkProposal(b, { role, identities, dispatched })
    if (!gate.ok) throw new R.BookingError(gate.reason)

    // Only the TARGET day: a hold never touches the day the booking is on.
    const daySnap = await t.get(DAY_REF(date))
    const day = daySnap.exists ? daySnap.data() : Object.assign({}, S.EMPTY_COUNTS)

    // An out-of-area booking holds a seat on the route going there that day.
    // A same-day slot change stays on the route it is already on and takes no
    // second seat -- and must not read the route, which it would not write.
    const entry = (day.tripByProvince || {})[R.provinceOf(b)]
    const toId = b.outOfArea ? (entry && entry.tripId) || null : null
    const sameTrip = !!toId && toId === b.tripId
    const toRef = toId && !sameTrip ? TRIP_COL().doc(toId) : null
    const toSnap = toRef ? await t.get(toRef) : null
    const toTrip = toSnap && toSnap.exists ? toSnap.data() : null

    const target = X.checkProposalTarget(b, {
      date, slot, role, today: R.todayStr(), leadDays: cfg.leadDays, horizonDays: cfg.horizonDays,
      day, caps: R.capacityForDay(day, cfg.cars, cfg.perCar), toId, sameTrip, toTrip
    })
    if (!target.ok) throw new R.BookingError(target.reason)

    const dayPatch = Object.assign({ date }, R.countsAfterAdd(day, slot, b.outOfArea))
    // checkProposalTarget refused a missing trip, so toTrip is real here.
    if (toRef) Object.assign(dayPatch, S.applyTripDelta(t, day, toRef, toId, R.tripAfterSeat(toTrip)))
    t.set(DAY_REF(date), dayPatch, { merge: true })

    const proposal = X.newProposal({
      kind: 'move', date, slot, tripId: toId, holdsTripSeat: !!toRef, reason, note,
      by: user || '', at: new Date().toISOString(), planned: gate.warn === 'PLANNED'
    })
    t.update(bRef, X.proposePatch(b, proposal))
    result = { planned: proposal.planned }
  })
  return result
}

async function proposeCancel (bookingId, { reason, note, user, role, identities }) {
  if (!String(reason || '').trim()) throw new R.BookingError('NO_REASON')
  const dispatched = await isDispatched((await preRead(bookingId)).soNo)
  const bRef = SLOT_COL().doc(bookingId)
  let result = {}

  await db.runTransaction(async t => {
    const bSnap = await t.get(bRef)
    if (!bSnap.exists) throw new R.BookingError('NOT_FOUND')
    const b = bSnap.data()
    const gate = X.checkProposal(b, { role, identities, dispatched })
    if (!gate.ok) throw new R.BookingError(gate.reason)
    const proposal = X.newProposal({
      kind: 'cancel', reason, note, by: user || '', at: new Date().toISOString(), planned: gate.warn === 'PLANNED'
    })
    t.update(bRef, X.proposePatch(b, proposal))
    result = { planned: proposal.planned }
  })
  return result
}

// Give back the seat a move proposal holds. Reads, then writes; the caller has
// read the booking already and writes it after.
async function releaseHold (t, b) {
  const p = b.proposal
  if (p.kind !== 'move') return
  const daySnap = await t.get(DAY_REF(p.date))
  const day = daySnap.exists ? daySnap.data() : Object.assign({}, S.EMPTY_COUNTS)
  const toRef = p.holdsTripSeat && p.tripId ? TRIP_COL().doc(p.tripId) : null
  const toSnap = toRef ? await t.get(toRef) : null
  const toTrip = toSnap && toSnap.exists ? toSnap.data() : null

  const dayPatch = Object.assign({ date: p.date }, R.countsAfterRemove(day, p.slot, b.outOfArea))
  if (toRef && toTrip) Object.assign(dayPatch, S.applyTripDelta(t, day, toRef, p.tripId, R.tripAfterRelease(toTrip)))
  else if (toRef) t.set(toRef, {}, { merge: true })
  t.set(DAY_REF(p.date), dayPatch, { merge: true })
}

async function confirmProposal (bookingId, ctx) {
  const pre = await preRead(bookingId)
  // Planning is blocked while a proposal is pending, but carBooking can still
  // put a planned booking on a sheet by hand. Refused, not silently moved.
  if (await isDispatched(pre.soNo)) throw new R.BookingError('DISPATCHED')
  const bRef = SLOT_COL().doc(bookingId)
  let result = {}

  await db.runTransaction(async t => {
    const bSnap = await t.get(bRef)
    if (!bSnap.exists) throw new R.BookingError('NOT_FOUND')
    const b = bSnap.data()
    if (!R.isActive(b.status)) throw new R.BookingError('ALREADY_CANCELLED')
    if (!X.isPending(b)) throw new R.BookingError('NO_PROPOSAL')
    if (!X.canDecide(b, deciderCtx(b, ctx))) throw new R.BookingError('NOT_DECIDER')
    // A move whose target day has passed while it sat pending cannot be
    // confirmed onto a day that is gone -- the panel must close it another
    // way (reject/withdraw), not carry it forward to a date nobody can serve.
    if (b.proposal.kind === 'move' && b.proposal.date < R.todayStr()) throw new R.BookingError('EXPIRED')

    const p = b.proposal
    const now = new Date().toISOString()
    const decided = { status: 'confirmed', decidedBy: ctx.user || '', decidedAt: now }
    // `p.planned` is only true when the booking was ALREADY planned at the
    // moment the proposal was filed (checkProposal's PLANNED warning). A date
    // can also be planned afterwards, while the proposal sat pending, and that
    // plan is just as stale on confirm -- so the decider must be told to
    // re-plan either way.
    result = { kind: p.kind, planned: !!p.planned || b.planVersion != null, date: p.date, slot: p.slot, from: b.date }

    if (p.kind === 'cancel') {
      // An up-country order sits on no day: its cancel leaves its trip, if any.
      const patch = U.isUpcountry(b)
        ? await US.upCancelWrites(t, Object.assign({ id: bookingId }, b), { user: ctx.user, reason: p.reason })
        : await S.cancelWrites(t, b, { user: ctx.user, reason: p.reason })
      t.update(bRef, Object.assign(patch, X.decisionPatch(b, decided, false)))
      return
    }

    // The target day has counted this seat since the proposal was made, so it
    // is not read. Only the old day (and the old route, if the booking is
    // leaving it) gives one back.
    const oldSnap = await t.get(DAY_REF(b.date))
    const oldDay = oldSnap.exists ? oldSnap.data() : Object.assign({}, S.EMPTY_COUNTS)

    // A move leaves the booking's visit, and gives its old seat back only as
    // the visit's last member on that day -- decided now, not when proposed,
    // because the others may have been cancelled or moved meanwhile.
    const out = V.leave(oldDay, b)
    // Routes as NET seats. The old route gives one back if the booking held one
    // (keyed on the booking's own tripId, not on `holdsTripSeat`: attachSo can
    // move a booking in-area after the proposal was made, and that old route's
    // seat must not stay reserved). A same-route slot change took no hold when
    // proposed -- it expected to carry its seat over -- so it takes one now,
    // which nets to nothing unless the booking had no seat of its own to carry.
    const deltas = V.tripDeltas([
      { tripId: b.tripId, delta: out.releasesSeat ? -1 : 0 },
      { tripId: p.tripId && !p.holdsTripSeat ? p.tripId : null, delta: 1 }
    ])
    const tripIds = Object.keys(deltas)
    const tripSnaps = {}
    for (let i = 0; i < tripIds.length; i++) {
      tripSnaps[tripIds[i]] = await t.get(TRIP_COL().doc(tripIds[i]))
    }

    const dayPatch = { date: b.date }
    if (out.releasesSeat) Object.assign(dayPatch, R.countsAfterRemove(oldDay, b.slot, b.outOfArea))
    if (out.visits) dayPatch.visits = out.visits
    tripIds.forEach(id => {
      const ref = TRIP_COL().doc(id)
      const snap = tripSnaps[id]
      if (!snap.exists) { t.set(ref, {}, { merge: true }); return }
      let next = snap.data()
      const d = deltas[id]
      for (let k = 0; k < Math.abs(d); k++) next = d > 0 ? R.tripAfterSeat(next) : R.tripAfterRelease(next)
      // A same-route change is a same-day change, so the old day holds the
      // mirror either way.
      Object.assign(dayPatch, S.applyTripDelta(t, oldDay, ref, id, next))
    })
    t.set(DAY_REF(b.date), dayPatch, { merge: true })

    t.update(bRef, Object.assign({
      date: p.date,
      slot: p.slot,
      tripId: p.tripId || null,
      visitId: null,
      movedAt: now,
      movedBy: ctx.user || '',
      history: (b.history || []).concat([{
        action: 'move',
        from: { date: b.date, slot: b.slot },
        to: { date: p.date, slot: p.slot },
        reason: p.reason,
        note: p.note || '',
        by: ctx.user || '',
        proposedBy: p.by,
        at: now
      }])
    }, X.decisionPatch(b, decided, false)))
  })
  return result
}

async function rejectProposal (bookingId, ctx) {
  if (!String(ctx.reason || '').trim()) throw new R.BookingError('NO_REJECT_REASON')
  const bRef = SLOT_COL().doc(bookingId)
  await db.runTransaction(async t => {
    const bSnap = await t.get(bRef)
    if (!bSnap.exists) throw new R.BookingError('NOT_FOUND')
    const b = bSnap.data()
    if (!X.isPending(b)) throw new R.BookingError('NO_PROPOSAL')
    if (!X.canDecide(b, deciderCtx(b, ctx))) throw new R.BookingError('NOT_DECIDER')
    const now = new Date().toISOString()
    const told = !!ctx.customerTold
    const decided = { status: 'rejected', decidedBy: ctx.user || '', decidedAt: now,
      rejectReason: String(ctx.reason).trim(), customerTold: told }
    if (told) Object.assign(decided, { toldBy: ctx.user || '', toldAt: now })
    await releaseHold(t, b)
    t.update(bRef, X.decisionPatch(b, decided, !told))
  })
}

async function withdrawProposal (bookingId, ctx) {
  const bRef = SLOT_COL().doc(bookingId)
  await db.runTransaction(async t => {
    const bSnap = await t.get(bRef)
    if (!bSnap.exists) throw new R.BookingError('NOT_FOUND')
    const b = bSnap.data()
    if (!X.isPending(b)) throw new R.BookingError('NO_PROPOSAL')
    if (!X.canWithdraw(b, deciderCtx(b, ctx))) throw new R.BookingError('NOT_PROPOSER')
    await releaseHold(t, b)
    t.update(bRef, X.decisionPatch(b, { status: 'withdrawn', decidedBy: ctx.user || '',
      decidedAt: new Date().toISOString() }, false))
  })
}

async function markCustomerTold (bookingId, ctx) {
  const bRef = SLOT_COL().doc(bookingId)
  await db.runTransaction(async t => {
    const bSnap = await t.get(bRef)
    if (!bSnap.exists) throw new R.BookingError('NOT_FOUND')
    const b = bSnap.data()
    if (!X.canMarkTold(b, deciderCtx(b, ctx))) throw new R.BookingError('NO_PROPOSAL')
    t.update(bRef, X.decisionPatch(b, { customerTold: true, toldBy: ctx.user || '',
      toldAt: new Date().toISOString() }, false))
  })
}

// Every open proposal, for the panel, the badge and the planner block. A
// single-field `in` is served by the automatic index -- no composite index.
function subscribeOpen (cb, onError) {
  return SLOT_COL()
    .where('proposalStatus', 'in', [X.PENDING, X.REJECTED_UNTOLD])
    .onSnapshot(snap => {
      const rows = []
      snap.forEach(d => rows.push(Object.assign({ id: d.id }, d.data())))
      cb(rows)
    }, onError)
}

// The booking the ขอเลื่อน dialog judges its chips against: slotChoices needs
// the order date, the out-of-area flag and the province, which the search card
// does not carry. null when it is gone.
async function loadBooking (bookingId) {
  const snap = await SLOT_COL().doc(bookingId).get()
  return snap.exists ? Object.assign({ id: snap.id }, snap.data()) : null
}

module.exports = {
  loadBooking,
  isDispatched, proposeMove, proposeCancel, confirmProposal, rejectProposal,
  withdrawProposal, markCustomerTold, subscribeOpen
}
